import { COMPOSE_PARAM } from './composeIntent';

/**
 * Which handheld screen a URL is (ADR 0048 §4).
 *
 * The URL already says everything the phone shell needs to know — which pane
 * is on screen, whether the composer is over it — so this is a pure function of
 * it rather than state any route has to report. `AppShell` writes the result to
 * `.shell[data-screen]`, and CSS keys the chrome on it: the bottom bar and the
 * FAB exist only on `list` and `calendar`; everything else is a screen you came
 * *into*, whose way out is ← and whose bottom edge belongs to its content.
 *
 * - `list`: a mail folder, or the contacts list.
 * - `message`: one message (`/mail/:folderId/:messageId`).
 * - `compose`: the compose sheet is open (`?compose=`), whatever is under it.
 * - `detail`: one contact (`/contacts/:contactId`), or one task
 *   (`/tasks/:listId/:taskId`). A task list is a `list`.
 * - `settings-index` / `settings-section`: `/settings`, `/settings/:section`.
 * - `calendar`: any calendar view. Event detail and the editor are state, not
 *   URL, and are sheets over this screen.
 */
export type Screen =
  | 'list'
  | 'message'
  | 'compose'
  | 'detail'
  | 'settings-index'
  | 'settings-section'
  | 'calendar';

export function screenFor(pathname: string, search: string): Screen {
  const segments = pathname.split('/').filter(Boolean);
  const [root] = segments;

  if (root === 'mail') {
    // Compose wins over what is underneath: it is a full-screen sheet, and the
    // bar under it would otherwise be a second way to navigate away mid-draft.
    if (new URLSearchParams(search).has(COMPOSE_PARAM)) return 'compose';
    // A label's view is `/mail/label/<account>/<label>`: a list, until a
    // message is opened one segment further (plan/17 §5).
    if (segments[1] === 'label') return segments.length >= 5 ? 'message' : 'list';
    return segments.length >= 3 ? 'message' : 'list';
  }
  if (root === 'calendar') return 'calendar';
  if (root === 'contacts') return segments.length >= 2 ? 'detail' : 'list';
  // `/tasks/:listId` is a list with the bar and the FAB; one task open
  // (`/tasks/:listId/:taskId`) is a screen you came into, with ←.
  if (root === 'tasks') return segments.length >= 3 ? 'detail' : 'list';
  if (root === 'settings') return segments.length >= 2 ? 'settings-section' : 'settings-index';
  // The admin area draws its own app bar with a ← like a settings section,
  // and has no bottom bar: it is somewhere you went, not a product.
  if (root === 'admin') return 'settings-section';
  // The not-found view and the index redirect: a top-level screen with the bar,
  // so there is always a way on to a product.
  return 'list';
}

/** Screens that show the bottom navigation bar and the FAB. */
export function hasBottomNav(screen: Screen): boolean {
  return screen === 'list' || screen === 'calendar';
}
